'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { Centrifuge, SubscriptionState, type Subscription, type PublicationContext } from 'centrifuge';
import { useApiClient } from './ApiContext';
import { formatApiError } from '../utils/apiError';

const CHANNEL_PREFIX = process.env.NEXT_PUBLIC_CENTRIFUGO_CHANNEL_PREFIX || '';

const IS_DEBUG = process.env.NODE_ENV !== 'production';

const debugLog = (...args: unknown[]): void => {
  if (IS_DEBUG) console.log(...args);
};

const RECONNECT_STORM_THRESHOLD = 25;

// WHY: 103 permission denied, 102 unknown channel — сервер не передумает, повтор бессмысленен
const PERMANENT_SUBSCRIPTION_CODES = new Set([102, 103]);

const isPermanentSubscriptionError = (ctx: { error?: { code?: number } }): boolean =>
  typeof ctx.error?.code === 'number' && PERMANENT_SUBSCRIPTION_CODES.has(ctx.error.code);

export interface CentrifugoContextValue {
  isConnected: boolean;
  channelPrefix: string;
  subscribe: (channel: string, onMessage: (data: unknown) => void) => () => void;
}

export const CentrifugoContext = createContext<CentrifugoContextValue | null>(null);

interface CentrifugoProviderProps {
  children: ReactNode;
  anonymous?: boolean;
}

export const CentrifugoProvider: React.FC<CentrifugoProviderProps> = ({ children, anonymous = false }) => {
  const apiClient = useApiClient();
  const [isConnected, setIsConnected] = useState(false);
  const [channelPrefix, setChannelPrefix] = useState(CHANNEL_PREFIX);
  const centrifugeRef = useRef<Centrifuge | null>(null);
  const subscriptionsRef = useRef<Map<string, Subscription>>(new Map());
  const handlerCountRef = useRef<Map<string, number>>(new Map());
  const deniedChannelsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let wsUrl = process.env.NEXT_PUBLIC_CENTRIFUGO_WS_URL;

    if (!wsUrl) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/centrifugo/connection/websocket`;
    }

    const fetchToken = async () => {
      const response = anonymous
        ? await apiClient.api.realtimeControllerGetPublicToken()
        : await apiClient.api.realtimeControllerGetToken();
      setChannelPrefix(response.channelPrefix);
      return response.token;
    };

    const centrifuge = new Centrifuge(wsUrl, {
      getToken: async () => {
        try {
          return await fetchToken();
        } catch (error) {
          console.error('Failed to get Centrifugo token:', formatApiError(error));
          throw error;
        }
      },
    });

    centrifuge.on('connected', (ctx) => {
      debugLog('[Centrifugo] ✅ Connected', ctx);
      setIsConnected(true);
    });

    centrifuge.on('disconnected', (ctx) => {
      debugLog('[Centrifugo] ⚠️ Disconnected', ctx);
      setIsConnected(false);
    });

    centrifuge.on('connecting', (ctx) => {
      debugLog('[Centrifugo] 🔄 Connecting...', ctx);
    });

    let transportErrors = 0;
    centrifuge.on('error', (ctx) => {
      transportErrors += 1;
      if (transportErrors === RECONNECT_STORM_THRESHOLD) {
        console.error(`Centrifugo reconnect storm: ${transportErrors} обрывов за сессию`, ctx);
        return;
      }
      if (transportErrors < RECONNECT_STORM_THRESHOLD) console.warn('Centrifugo error:', ctx);
    });

    centrifugeRef.current = centrifuge;
    centrifuge.connect();

    return () => {
      subscriptionsRef.current.forEach((sub) => sub.unsubscribe());
      subscriptionsRef.current.clear();
      handlerCountRef.current.clear();
      centrifuge.disconnect();
    };
  // WHY: `anonymous` MUST stay in deps: the flip needs a reconnect with the private token, or user#<id> never authorizes
  }, [apiClient, anonymous]);

  const subscribe = useCallback((channel: string, onMessage: (data: unknown) => void) => {
    const centrifuge = centrifugeRef.current;
    if (!centrifuge) {
      console.warn('[Centrifugo] Cannot subscribe, client not initialized');
      return () => {};
    }

    debugLog(`[Centrifugo] 📡 Attempting to subscribe to: ${channel}`);

    if (deniedChannelsRef.current.has(channel)) return () => {};

    // WHY: centrifuge-js keeps its OWN registry; newSubscription() on a channel still there throws "already exists"
    let subscription = subscriptionsRef.current.get(channel) ?? centrifuge.getSubscription(channel) ?? undefined;

    if (!subscription) {
      subscription = centrifuge.newSubscription(channel);

      subscription.on('subscribed', (ctx) => {
        debugLog(`[Centrifugo] ✅ Successfully subscribed to: ${channel}`, ctx);
      });
      subscription.on('subscribing', (ctx) => {
        debugLog(`[Centrifugo] 🔄 Subscribing to: ${channel}`, ctx);
      });
      subscription.on('unsubscribed', (ctx) => {
        debugLog(`[Centrifugo] ⚠️ Unsubscribed from: ${channel}`, ctx);
      });
      subscription.on('error', (ctx) => {
        if (isPermanentSubscriptionError(ctx)) {
          deniedChannelsRef.current.add(channel);
          console.warn(`[Centrifugo] подписка на ${channel} отклонена окончательно:`, ctx.error);
          return;
        }
        debugLog(`[Centrifugo] ❌ Subscription error on ${channel}:`, ctx);
      });
    } else {
      debugLog(`[Centrifugo] ♻️ Reusing existing subscription: ${channel}`);
    }

    subscriptionsRef.current.set(channel, subscription);
    if (subscription.state === SubscriptionState.Unsubscribed) {
      subscription.subscribe();
    }

    const handler = (ctx: PublicationContext) => {
      debugLog(`[Centrifugo] 📨 Message received on ${channel}`, ctx.data);
      onMessage(ctx.data);
    };

    subscription.on('publication', handler);
    handlerCountRef.current.set(channel, (handlerCountRef.current.get(channel) ?? 0) + 1);
    console.log(`[Centrifugo] ✅ Handler attached to ${channel}`);

    return () => {
      console.log(`[Centrifugo] 🔌 Removing handler from ${channel}`);
      const sub = subscriptionsRef.current.get(channel);
      sub?.removeListener('publication', handler);
      const remaining = (handlerCountRef.current.get(channel) ?? 1) - 1;
      if (remaining <= 0) {
        // WHY: unsubscribe() leaves the channel in the client registry — next subscribe() throws "already exists"; use
        // removeSubscription()
        if (sub) centrifugeRef.current?.removeSubscription(sub);
        subscriptionsRef.current.delete(channel);
        handlerCountRef.current.delete(channel);
      } else {
        handlerCountRef.current.set(channel, remaining);
      }
    };
  }, []);

  return (
    <CentrifugoContext.Provider value={{ isConnected, channelPrefix, subscribe }}>
      {children}
    </CentrifugoContext.Provider>
  );
};

export const useCentrifugo = (): CentrifugoContextValue => {
  const context = useContext(CentrifugoContext);
  if (!context) {
    return {
      isConnected: false,
      channelPrefix: CHANNEL_PREFIX,
      subscribe: () => () => {},
    };
  }
  return context;
};
